// src/pages/Telephony/Extensions.tsx
//
// SIP seat management for one organization.
//
// Extension numbers are unique WITHIN an organization, not globally — every
// tenant is free to use 1001. Isolation comes from the dialplan, where each
// organization gets its own context containing only its own extensions, so
// another tenant's 1001 is not reachable rather than merely forbidden.
//
// A seat may belong to a user or stand alone: an agent's softphone is tied to a
// person, while a reception handset or meeting-room device outlives whoever is
// using it today.
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, KeyRound, Power, ShieldAlert, Copy, Check, PhoneForwarded, PhoneOutgoing, X } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import axiosInstance from 'api/axios.config';
import { API_ENDPOINTS } from 'api/endpoints';

const ALLOWED_ROLES = ['master_admin', 'super_admin', 'service_admin', 'provider_owner', 'provider_admin'];

type Extension = {
  id: string;
  organization_id: string;
  extension: string;
  display_name?: string;
  user_id?: string | null;
  sip_username: string;
  enabled: boolean;
  follow_me_enabled?: boolean;
};

/** One hop in a follow-me chain. Order in the array IS the order rung. */
type FollowMeTarget = { number: string; ring_seconds: number };

/** A password the server has just issued. It is shown once and never retrievable. */
type Issued = { sipUsername: string; sipPassword: string };

const Extensions: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = (currentOrganization?.role || user?.role || '').toLowerCase();
  const allowed = ALLOWED_ROLES.includes(role);

  // The organization every request acts on.
  //
  // Sent explicitly rather than relying on the X-Organization-Id header: that
  // header is only attached when localStorage holds a selected organization, so
  // a freshly logged-in master admin sends nothing and the API correctly answers
  // "no organization in context". Passing it here removes the ambiguity.
  const orgId = currentOrganization?.id;

  const [rows, setRows] = useState<Extension[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newExt, setNewExt] = useState('');
  const [newName, setNewName] = useState('');
  const [issued, setIssued] = useState<Issued | null>(null);
  const [copied, setCopied] = useState(false);
  const [followMeFor, setFollowMeFor] = useState<Extension | null>(null);
  const [callerIdsFor, setCallerIdsFor] = useState<Extension | null>(null);

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true); setError(null);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.EXTENSIONS, { params: { organizationId: orgId } });
      const d = res.data?.data ?? res.data;
      setRows(Array.isArray(d?.extensions) ? d.extensions : Array.isArray(d) ? d : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'could not load extensions');
    } finally {
      setLoading(false);
    }
  }, [allowed, orgId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setError(null);
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.EXTENSIONS, {
        organizationId: orgId,
        extension: newExt.trim(),
        displayName: newName.trim() || undefined,
      });
      const d = res.data?.data ?? res.data;
      // The password exists in this response and nowhere else, ever again.
      if (d?.sipPassword) setIssued({ sipUsername: d.sipUsername, sipPassword: d.sipPassword });
      setNewExt(''); setNewName('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'could not create extension');
    }
  };

  const toggle = async (r: Extension) => {
    setError(null);
    try {
      await axiosInstance.put(API_ENDPOINTS.EXTENSION_ENABLED(r.id), { organizationId: orgId, enabled: !r.enabled });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'could not change state');
    }
  };

  const resetPassword = async (r: Extension) => {
    setError(null);
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.EXTENSION_RESET_PASSWORD(r.id), { organizationId: orgId });
      const d = res.data?.data ?? res.data;
      if (d?.sipPassword) setIssued({ sipUsername: d.sipUsername, sipPassword: d.sipPassword });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'could not reset password');
    }
  };

  const remove = async (r: Extension) => {
    setError(null);
    try {
      await axiosInstance.delete(API_ENDPOINTS.EXTENSION_DELETE(r.id), { params: { organizationId: orgId } });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'could not delete extension');
    }
  };

  if (!allowed) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Restricted</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Extension management is available to organization admins.
          </p>
        </div>
      </div>
    );
  }

  if (!orgId) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Select an organization</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Extensions belong to an organization, so one has to be selected before they can be
            listed or created. Use the organization switcher at the top of the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Extensions</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Internal SIP seats for {currentOrganization?.name || 'your organization'}. Numbers are unique
            within your organization — other tenants may use the same ones and cannot reach yours.
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 inline-flex items-center gap-2 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Shown once. The server stores only an encrypted copy and cannot return
          it again, so a lost credential is replaced rather than recovered. */}
      {issued && (
        <div className="rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30 p-4">
          <p className="text-sm font-medium text-green-900 dark:text-green-200">
            Save this password now — it cannot be shown again.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 px-3 py-2 rounded bg-white dark:bg-gray-800 font-mono text-sm text-gray-900 dark:text-gray-100 break-all">
              {issued.sipUsername} / {issued.sipPassword}
            </code>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(`${issued.sipUsername} / ${issued.sipPassword}`);
                setCopied(true); setTimeout(() => setCopied(false), 2000);
              }}
              className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm inline-flex items-center gap-1.5">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
            <button onClick={() => setIssued(null)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300">
              Done
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Add a seat</h2>
        <div className="flex flex-wrap gap-2">
          <input value={newExt} onChange={e => setNewExt(e.target.value)} placeholder="extension (e.g. 1001)"
            className="w-44 px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 font-mono" />
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="name (optional)"
            className="flex-1 min-w-[12rem] px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100" />
          <button onClick={create} disabled={!newExt.trim()}
            className="tl-btn tl-btn-primary disabled:opacity-40">
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          2–8 digits. A seat with no user attached is fine — use that for a shared handset or a meeting room.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Extension</th>
                <th className="text-left px-4 py-2.5 font-medium">Name</th>
                <th className="text-left px-4 py-2.5 font-medium">SIP username</th>
                <th className="text-left px-4 py-2.5 font-medium">State</th>
                <th className="text-left px-4 py-2.5 font-medium">Follow-me</th>
                <th className="text-right px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  {loading ? 'loading…' : 'no extensions yet'}
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-2.5 font-mono text-gray-900 dark:text-gray-100">{r.extension}</td>
                  <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{r.display_name || '—'}</td>
                  {/* Derived, never chosen: Asterisk endpoint names are global, so
                      the tenant is carried in the username. */}
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{r.sip_username}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${r.enabled
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {r.enabled ? 'enabled' : 'disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {/* Shown for every seat, not only enabled ones: "off" is the
                        answer to "why did it not ring my mobile", and hiding it
                        makes that unanswerable from this page. */}
                    <span className={`px-2 py-0.5 rounded-full text-xs ${r.follow_me_enabled
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                      {r.follow_me_enabled ? 'on' : 'off'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => setCallerIdsFor(r)} title="Caller IDs this seat may present"
                        className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                        <PhoneOutgoing className="w-4 h-4" />
                      </button>
                      <button onClick={() => setFollowMeFor(r)} title="Follow-me numbers"
                        className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                        <PhoneForwarded className="w-4 h-4" />
                      </button>
                      <button onClick={() => toggle(r)} title={r.enabled ? 'Disable' : 'Enable'}
                        className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                        <Power className="w-4 h-4" />
                      </button>
                      <button onClick={() => resetPassword(r)} title="Issue a new password"
                        className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(r)} title="Delete — prefer disabling, which keeps the number reserved"
                        className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {callerIdsFor && (
        <CallerIdPanel seat={callerIdsFor} orgId={orgId}
          onClose={() => setCallerIdsFor(null)}
          onSaved={() => setCallerIdsFor(null)} />
      )}

      {followMeFor && (
        <FollowMePanel seat={followMeFor} orgId={orgId}
          onClose={() => setFollowMeFor(null)}
          onSaved={() => { setFollowMeFor(null); load(); }} />
      )}
    </div>
  );
};

/**
 * Follow-me numbers for one seat.
 *
 * The chain is saved whole rather than one entry at a time, because order is
 * the behaviour: the server derives positions from the array, so there is no
 * way for the UI to leave a gap or a duplicate behind.
 */
const FollowMePanel: React.FC<{
  seat: Extension;
  orgId?: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ seat, orgId, onClose, onSaved }) => {
  const [targets, setTargets] = useState<FollowMeTarget[]>([]);
  const [enabled, setEnabled] = useState(!!seat.follow_me_enabled);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.EXTENSION_FOLLOW_ME(seat.id), {
          params: { organizationId: orgId },
        });
        const rows = res.data?.data ?? [];
        setTargets(rows.map((t: any) => ({ number: t.number, ring_seconds: t.ring_seconds ?? 25 })));
      } catch (e: any) {
        setErr(e.response?.data?.message || 'could not load follow-me numbers');
      }
    })();
  }, [seat.id, orgId]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const clean = targets.filter(t => t.number.trim() !== '');
      await axiosInstance.put(API_ENDPOINTS.EXTENSION_FOLLOW_ME(seat.id), clean, {
        params: { organizationId: orgId },
      });
      // Enabling is a separate call and deliberately AFTER the numbers: the
      // server refuses to enable an empty chain, because a seat with follow-me
      // on and nothing to chase looks configured and behaves like today.
      await axiosInstance.post(API_ENDPOINTS.EXTENSION_FOLLOW_ME_ENABLED(seat.id), null, {
        params: { organizationId: orgId, enabled: enabled && clean.length > 0 },
      });
      onSaved();
    } catch (e: any) {
      setErr(e.response?.data?.message || 'could not save');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white">
              Follow-me — extension {seat.extension}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              If the desk seat does not answer, these numbers are rung in order.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        {err && (
          <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
            {err}
          </div>
        )}

        {/* Said plainly, because it is the part that surprises people: the
            person answering has to press a key, and that is what stops a
            switched-off mobile's voicemail swallowing the call. */}
        <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
          Each number is called on your organization's trunk and is billed. Whoever answers must
          press <strong>1</strong> to accept — without that, an unattended phone's voicemail would
          answer and the caller would be handed to a mailbox.
        </div>

        <div className="space-y-2">
          {targets.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-gray-400 w-4">{i + 1}</span>
              <input value={t.number} placeholder="+919000000000"
                onChange={e => setTargets(targets.map((x, j) => j === i ? { ...x, number: e.target.value } : x))}
                className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent text-sm" />
              <input type="number" value={t.ring_seconds} min={5} max={120}
                onChange={e => setTargets(targets.map((x, j) => j === i ? { ...x, ring_seconds: Number(e.target.value) } : x))}
                className="w-20 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent text-sm" />
              <span className="text-xs text-gray-400">sec</span>
              <button onClick={() => setTargets(targets.filter((_, j) => j !== i))}
                className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {targets.length < 5 && (
            <button onClick={() => setTargets([...targets, { number: '', ring_seconds: 25 }])}
              className="text-sm text-blue-600 dark:text-blue-400 inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Add a number
            </button>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Follow-me is on for this seat
        </label>

        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-gray-500">Takes effect on the next config push.</span>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300">
              Cancel
            </button>
            <button onClick={save} disabled={busy}
              className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** One number the seat could present, as the API reports it. */
type Selectable = { e164: string; granted: boolean; isDefault: boolean };

/**
 * Which of the organization's numbers this seat may present when it dials out.
 *
 * A number may be given to any number of seats (e.g. two numbers shared by four agents).
 * No numbers selected = the seat keeps the organization's single caller ID, exactly as
 * before. The agent chooses per call with a SIP header X-Caller-Id, or by dialling *8N
 * before the number (N = the position below). The switch only accepts numbers listed here.
 */
const CallerIdPanel: React.FC<{
  seat: Extension;
  orgId?: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ seat, orgId, onClose, onSaved }) => {
  const [options, setOptions] = useState<Selectable[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [def, setDef] = useState<string>('');
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.EXTENSION_CALLER_IDS(seat.id), { params: { organizationId: orgId } });
        const d = res.data?.data ?? res.data;
        const list: Selectable[] = Array.isArray(d?.callerIds) ? d.callerIds : [];
        setOptions(list);
        setChosen(list.filter(o => o.granted).map(o => o.e164));
        setDef(list.find(o => o.isDefault)?.e164 || '');
        setNote(d?.note || null);
      } catch (e: any) {
        setErr(e?.response?.data?.message || e?.message || 'could not load caller IDs');
      }
    })();
  }, [seat.id, orgId]);

  // Slot order is ascending number order, the same as the switch's *8N codes.
  const slots = [...chosen].sort();
  const toggle = (n: string) => {
    const next = chosen.includes(n) ? chosen.filter(x => x !== n) : [...chosen, n];
    setChosen(next);
    if (!next.includes(def)) setDef('');
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await axiosInstance.put(API_ENDPOINTS.EXTENSION_CALLER_IDS(seat.id),
        { callerIds: slots, default: def || null }, { params: { organizationId: orgId } });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white">
              Caller IDs — extension {seat.extension}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              The numbers this seat may show when it calls out. A number can be shared by several seats.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        {err && (
          <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
            {err}
          </div>
        )}
        {options.length === 0 && !err && (
          <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
            {note || 'No numbers can be presented from this seat yet.'}
          </div>
        )}

        {options.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-1.5">Allowed</th><th>Number</th><th>Dial code</th><th className="text-right">Default</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {options.map(o => {
                const on = chosen.includes(o.e164);
                const slot = slots.indexOf(o.e164) + 1;
                return (
                  <tr key={o.e164}>
                    <td className="py-2"><input type="checkbox" checked={on} onChange={() => toggle(o.e164)} /></td>
                    <td className="font-mono text-gray-800 dark:text-gray-200">{o.e164}</td>
                    <td className="font-mono text-gray-500">{on && slot <= 9 ? `*8${slot}` : '—'}</td>
                    <td className="text-right">
                      <input type="radio" name="default" disabled={!on} checked={on && def === o.e164}
                        onChange={() => setDef(o.e164)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-xs text-gray-600 dark:text-gray-400 space-y-1">
          <div>To choose per call: dial <span className="font-mono">*8N</span> then the number (e.g. <span className="font-mono">*82+919876543210</span>), or send the SIP header <span className="font-mono">X-Caller-Id</span>.</div>
          <div>Nothing selected = this seat uses the organization's caller ID, as before. A number not listed here is never presented.</div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-gray-500">Takes effect on the next config push.</span>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300">
              Cancel
            </button>
            <button onClick={save} disabled={busy || options.length === 0}
              className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Extensions;
